import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { Menu, X, LogOut, LayoutDashboard, ShoppingBag } from "lucide-react";
import { useState } from "react";
import ResumeBookingPill from "./ResumeBookingPill";
import { getServices } from "../lib/servicesCache";

const LOGO = "/tresses_emblem.png";

const allLinks = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/packages", label: "Packages" },
    { to: "/membership", label: "Membership", authOnly: true },
    { to: "/shop", label: "Shop", requiresShop: true },
    { to: "/promotions", label: "Promotions" },
    { to: "/pricing", label: "Pricing" },
    { to: "/about", label: "About" },
];

export default function Navbar() {
    const { user, logout } = useAuth();
    const { count, setOpen: setCartOpen } = useCart();
    const { settings } = useSettings();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const shopEnabled = settings.shop_enabled !== false;
    const links = allLinks.filter((l) => (!l.requiresShop || shopEnabled) && (!l.authOnly || !!user));

    const onLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#FBF6F0]/85 border-b border-[#C29A5B]/10" data-testid="navbar">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3 shrink-0 mr-6" data-testid="navbar-logo">
                    <img src={LOGO} alt="Tresses" className="h-14 w-14 md:h-16 md:w-16 object-contain" />
                    <div className="leading-tight hidden sm:block">
                        <div className="font-serif-display text-2xl md:text-3xl tracking-wider gold-text">TRESSES</div>
                        <div className="text-[0.62rem] tracking-[0.3em] text-[#7A5262] uppercase mt-0.5">Unisex Salon</div>
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-5 2xl:gap-8">
                    {links.map((l) => {
                        const prefetch = (l.to === "/services" || l.to === "/packages" || l.to === "/")
                            ? () => { getServices().catch(() => {}); }
                            : undefined;
                        return (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase()}`}
                            onMouseEnter={prefetch}
                            onFocus={prefetch}
                            className={({ isActive }) =>
                                `text-xs uppercase tracking-[0.18em] font-medium transition-colors whitespace-nowrap ${isActive ? "text-[#8F622C]" : "text-[#7A5262] hover:text-[#3D0C1F]"}`
                            }
                        >
                            {l.label}
                        </NavLink>
                        );
                    })}
                </nav>

                <div className="hidden xl:flex items-center gap-3 shrink-0 ml-6">
                    <ResumeBookingPill />
                    {shopEnabled && (
                        <button onClick={() => setCartOpen(true)} className="relative text-[#7A5262] hover:text-[#8F622C] p-2" data-testid="nav-cart" aria-label="Cart">
                            <ShoppingBag size={18} />
                            {count > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 bg-[#6B1535] text-[#FFF8F2] text-[0.55rem] font-bold w-4 h-4 flex items-center justify-center rounded-full" data-testid="cart-count">{count}</span>
                            )}
                        </button>
                    )}
                    {user ? (
                        <>
                            <Link
                                to={user.role === "admin" ? "/admin" : "/dashboard"}
                                className="btn-outline-gold"
                                data-testid="nav-dashboard"
                            >
                                <LayoutDashboard size={14} /> {user.role === "admin" ? "Admin" : "Dashboard"}
                            </Link>
                            <button onClick={onLogout} className="text-[#7A5262] hover:text-[#8F622C] p-2" data-testid="nav-logout" aria-label="Logout">
                                <LogOut size={18} />
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="text-xs uppercase tracking-[0.22em] text-[#7A5262] hover:text-[#3D0C1F]" data-testid="nav-login">
                                Sign In
                            </Link>
                            <Link to="/book" className="btn-gold" data-testid="nav-book">
                                Book Now
                            </Link>
                        </>
                    )}
                </div>

                <button className="xl:hidden text-[#8F622C]" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
                    {open ? <X /> : <Menu />}
                </button>
            </div>

            {open && (
                <div className="xl:hidden border-t border-[#6B1535]/10 bg-[#FBF6F0]/95 px-6 py-6 flex flex-col gap-4" data-testid="mobile-menu">
                    {links.map((l) => (
                        <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm tracking-wider text-[#7A5262]" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                            {l.label}
                        </Link>
                    ))}
                    {user ? (
                        <>
                            <Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="btn-outline-gold w-fit">
                                {user.role === "admin" ? "Admin" : "Dashboard"}
                            </Link>
                            <button onClick={onLogout} className="text-left text-[#7A5262]">Logout</button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" onClick={() => setOpen(false)} className="text-sm tracking-wider text-[#7A5262]">Sign In</Link>
                            <Link to="/book" onClick={() => setOpen(false)} className="btn-gold w-fit">Book Now</Link>
                        </>
                    )}
                </div>
            )}
        </header>
    );
}
